import React, { useMemo, useState } from 'react'
import Modal from '../../../../components/Modal'
import Input from '../../../../components/Input'
import { Button } from '../../../../components/Button'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import TextField from '../../../../components/TextField'
import { useNavigate } from 'react-router-dom'
import { TestIds } from '../../../../testIds'
import { Loader } from '../../../../components/Loader'
import apiClient from '../../../../services/api-client'
import { useEmailPhoneName } from '../../../../util/useEmailPhoneName'
import { CartProductCard } from '../../../products/components/ProductCard/CartProductCard'
import { getUpdatedVariant } from '../../../cart/utils/getUpdatedVariant'
import { useQuoteModal } from '../QuoteModal/useQuoteModal'
import { Variant } from '../../../products/utils/useVariations'
import { CartItem } from '../../../cart/storage/cart.types'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'

const EnquiryModal = () => {
  const {
    email,
    setEmail,
    name,
    setName,
    phone,
    setPhone,
    notes,
    setNotes,
    emailError,
    phoneError,
    nameError,
    validateFields,
  } = useEmailPhoneName()

  const account = useSelector(selectCurrentFarmerAccount)

  const navigate = useNavigate()
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState(false)

  const handleSubmit = async () => {
    if (!product || !account) return
    if (!validateFields()) return

    setLoading(true)
    try {
      const data = {
        name,
        email,
        phone,
        notes,
        accountNumber: account.accountNumber,
        productId: product[0].productId,
        quantity,
      }
      const response = await apiClient.post('/product-enquiry', data)

      if (response.status.toString().startsWith('2')) {
        setLoading(false)
        navigate('/enquiry-request-sent')
      } else {
        setLoading(false)
        setError(true)
      }
    } catch (err) {
      setLoading(false)
      setError(true)
    }
  }
  const [quantity, setQuantity] = useState(1)
  const { product, setProduct } = useQuoteModal()

  const handleChangeVariant = (variant: Variant) => {
    if (product?.[0]) {
      setProduct(getUpdatedVariant(variant, product[0] as CartItem))
    }
  }

  const singleItemQuote = useMemo(() => {
    if (product?.length === 1) {
      return (
        <Box flex={'1 1 100%'}>
          <CartProductCard
            item={{ ...product[0], quantity, stockAvailabilities: [] } as CartItem}
            onChangeQuantity={setQuantity}
            enableRemove={false}
            enableVariants={true}
            onChangeVariant={handleChangeVariant}
          />
        </Box>
      )
    }
  }, [product, quantity])

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Modal
      title='Submit an Enquiry'
      open={true}
      maxWidth='md'
      handleClose={() => {
        setProduct(null)
      }}
      footer={
        <Button variant='contained' onClick={handleSubmit} sx={{ width: isSm ? '100%' : 'auto' }}>
          Enquire now
        </Button>
      }
      paperProps={{
        sx: {
          height: isSm ? 'inherit' : 'auto',
        },
      }}
    >
      <Box display='flex' flexDirection={'column'} gap={3}>
        {singleItemQuote}

        <Typography variant='body2'>Please provide your contact details</Typography>
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', columnGap: 2 }}>
          <Box sx={{ flex: '1 0 30%', minWidth: 200 }}>
            <Input
              label='Name:'
              required
              value={name}
              setValue={setName}
              errorMessage={nameError}
              type='fname'
              inputBaseProps={{ type: 'fname' }}
              testId={TestIds.feat.requestQuote.nameInput}
            />
          </Box>
          <Box sx={{ flex: '1 1 38%', minWidth: 200 }}>
            <Input
              label='Email address:'
              required
              value={email}
              setValue={setEmail}
              errorMessage={emailError}
              type='email'
              inputBaseProps={{ type: 'email', autoComplete: 'email' }}
              testId={TestIds.feat.requestQuote.emailInput}
            />
          </Box>
          <Box sx={{ flex: '1 0 28%', minWidth: 200 }}>
            <Input
              label='Phone number'
              required
              value={phone}
              setValue={setPhone}
              errorMessage={phoneError}
              type='tel'
              inputBaseProps={{ pattern: '[^\\s]*', title: 'Spaces are not allowed', type: 'tel' }}
              testId={TestIds.feat.requestQuote.phoneInput}
            />
          </Box>
        </Box>

        <Box sx={{ minHeight: 100 }}>
          <TextField
            aria-label='Notes or questions'
            label='Notes or questions:'
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            inputProps={{ maxLength: 200 }}
            maxRows={10}
            multiline
            minRows={3}
            variant='outlined'
            helperText={`${notes?.length ?? 0}/200 characters`}
            testId={TestIds.feat.requestQuote.notesInput}
          />
        </Box>

        {error && (
          <Typography variant='body2' color='error'>
            Something went wrong, please try again.
          </Typography>
        )}

        {loading && <Loader title='Sending enquiry' />}
      </Box>
    </Modal>
  )
}

export default EnquiryModal
